import type { Candle, Tick, Timeframe } from "../types";

function newCandle(time: number, t: Tick, prevPrice: number | null): Candle {
  const c: Candle = { time, endTime: t.time, open: t.price, high: t.price, low: t.price, close: t.price, volume: 1, upTicks: 0, downTicks: 0 };
  if (prevPrice != null) { if (t.price > prevPrice) c.upTicks = 1; else if (t.price < prevPrice) c.downTicks = 1; }
  return c;
}

function applyTick(c: Candle, t: Tick) {
  if (t.price > c.close) c.upTicks++;
  else if (t.price < c.close) c.downTicks++;
  if (t.price > c.high) c.high = t.price;
  if (t.price < c.low) c.low = t.price;
  c.close = t.price;
  c.endTime = t.time;
  c.volume++;
}

// Synthetic 1s tick candle (MT5-style): open = previous source tick, close = current source tick.
// high/low come ONLY from those two real prices; nothing is fabricated. The first tick seeds O=H=L=C.
function syntheticCandle(time: number, t: Tick, prev: Candle | undefined): Candle {
  if (!prev) return { time, endTime: t.time, open: t.price, high: t.price, low: t.price, close: t.price, volume: 1, upTicks: 0, downTicks: 0 };
  const o = prev.close, c = t.price;
  return {
    time, endTime: t.time, open: o, close: c, high: Math.max(o, c), low: Math.min(o, c), volume: 1,
    upTicks: c > o ? 1 : 0, downTicks: c < o ? 1 : 0,
  };
}

export const isSyntheticTf = (tf: Timeframe) => tf.kind === "sec" && tf.seconds === 1;

// Incremental tick → candle aggregation.
//   sec/candle (5s, 15s, 30s, 1m…) → conventional OHLC time buckets
//   sec 1s                          → SyntheticTickCandle: one candle per source tick
//   tick (10T, 25T…)                → N source ticks per bar
export class TickAggregator {
  candles: Candle[] = [];
  private count = 0;
  private lastTick: Tick | null = null;

  constructor(readonly tf: Timeframe, private maxCandles = 5000) {}

  reset(ticks: Tick[] = []) {
    this.candles = [];
    this.count = 0;
    this.lastTick = null;
    for (const t of ticks) this.push(t);
  }

  // Returns true when a new candle was opened.
  push(t: Tick): boolean {
    const last = this.candles[this.candles.length - 1];
    const prev = last ? last.close : null;
    if (isSyntheticTf(this.tf)) {
      const lt = this.lastTick;
      // duplicate identity = symbol + epoch + quote (reconnects / overlapping history must not create candles)
      if (lt && (t.time < lt.time || (t.time === lt.time && t.price === lt.price))) return false;
      this.lastTick = t;
      const time = last ? Math.max(Math.floor(t.time), last.time + 1) : Math.floor(t.time); // deterministic, strictly increasing
      this.candles.push(syntheticCandle(time, t, last));
      return this.trim(true);
    }
    if (this.tf.kind === "tick") {
      const n = this.tf.ticks!;
      const opensNew = this.count % n === 0;
      this.count++;
      if (opensNew || !last) this.candles.push(newCandle(t.time, t, prev));
      else applyTick(last, t);
      return this.trim(opensNew);
    }
    const s = this.tf.seconds!;
    const bucket = Math.floor(t.time / s) * s;
    if (!last || last.time !== bucket) {
      if (last && bucket < last.time) return false;
      this.candles.push(newCandle(bucket, t, prev));
      return this.trim(true);
    }
    applyTick(last, t);
    return false;
  }

  // Seed with server candles (>=1m granularity) then keep updating from live ticks.
  seed(candles: Candle[]) {
    this.candles = candles.map((c) => ({ ...c }));
    this.count = 0;
    this.lastTick = null;
  }

  private trim(opened: boolean): boolean {
    if (this.candles.length > this.maxCandles) this.candles.splice(0, this.candles.length - this.maxCandles);
    return opened;
  }
}
